/* eslint-disable import/no-cycle */
import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Injector,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { get as _get, isEmpty as _isEmpty } from 'lodash/fp'

import {
  Observable,
  combineLatest,
  debounceTime,
  filter,
  from,
  map,
  startWith,
  tap,
  throwError,
} from 'rxjs'

import { FlxWrapperStyleDirective } from '@flowx/angular-theme'
import {
  CollectionPrototypeInstanceMeta,
  ComponentIdentifierEnum,
  CustomComponentTemplateConfig,
  ProcessAction,
  TemplateConfig,
  buildUploadActionFormData,
  collectionPrefix,
  createAngularCollectionChildDecorationCache,
  decorateAngularCollectionChildren,
  deprecatedAlias,
  getActionsForTemplateConfig$,
  getCurrentReadOnlyState,
  getCustomComponents,
  isUserTaskRoot,
  reusableCustomComponentsMap,
  snapshotActionParams,
  templateSequenceStore,
  unfoldedProcessData$,
  unknownCustomComponentActionError,
  withDeprecatedSpread,
} from '@flowx/core-sdk'

import { FlxCustomComponentInputsDirective } from './custom-component-inputs.directive'

import { COMPONENT_WRAPPERS_MAP } from '../../components/component.dictionaries'
import { FlxOrderByPipe } from '../../pipes/order-by.pipe'
import { FlxFormsService } from '../../services/flx-forms.service'

export const FLX_VALIDATOR_SERVICE = new InjectionToken<ValidatorService>('FLX_VALIDATOR_SERVICE')
export type ValidatorService = {
  validated$: Observable<boolean>
  validate(isValid: boolean): void
  saveData(data: Record<string, any>): void
}

export type FlxCustomComponentData = {
  data: Record<string, any>
  actions: ProcessAction[]
  /** @deprecated Use `runAction` instead. Kept for backward compatibility; removal will be announced. */
  actionsFn: Record<string, (params?: Record<string, unknown> | FormData) => Observable<void>>
  /** Runs the named action as soon as it is called; errors when the component has no such action. */
  runAction: (action: string, params?: Record<string, unknown>) => Observable<void>
  /** Uploads `file` through the named upload action; `params` go beside it in the form data. */
  runUploadAction: (
    action: string,
    file: Blob,
    params?: Record<string, unknown>
  ) => Observable<void>
}

type ActionRunner = (params?: Record<string, unknown> | FormData) => Observable<void>

const hashCode = (str: string): string => {
  let hash = 0
  if (typeof str !== 'string' || str.length === 0) {
    return String(hash)
  }
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + char
    // eslint-disable-next-line no-bitwise
    hash = hash & hash // convert to 32bit integer
  }
  // eslint-disable-next-line no-bitwise
  hash = hash >>> 0 // convert signed to unsigned https://stackoverflow.com/a/1908655
  return Number(hash).toString(32).toUpperCase() // make the hash small, convert base10 to base32
}

@Component({
  selector: 'flx-tree-node',
  standalone: true,
  imports: [CommonModule, FlxOrderByPipe, FlxCustomComponentInputsDirective],
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  templateUrl: './tree-node.component.html',
  styleUrl: './tree-node.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTreeNodeComponent {
  #cdr = inject(ChangeDetectorRef)
  #injector = inject(Injector)
  // Lives and dies with this node, so decorations never outlive the tree they index.
  #decorationCache = createAngularCollectionChildDecorationCache()
  // Keyed on the child config object rather than its uuid because the uuid outlives the config: a
  // uuid key would still hit after a template ingest replaced the config and hand back a pipeline
  // closed over the stale one, so a child merged in by an advance would never appear. A config key
  // misses on exactly that transition, which is why no invalidation site is needed.
  #customComponentStreams = new WeakMap<
    CustomComponentTemplateConfig,
    Observable<FlxCustomComponentData>
  >()
  formService = inject(FlxFormsService)
  injectorMap = new Map<string, Injector>()
  templateConfig = input.required<TemplateConfig>()
  collectionPrototypeInstanceMeta = input<
    CollectionPrototypeInstanceMeta[] | CollectionPrototypeInstanceMeta[][]
  >()

  templateConfigWithCollectionMeta = computed<TemplateConfig[]>(() =>
    decorateAngularCollectionChildren({
      templateConfig: this.templateConfig().templateConfig,
      parentFlowxUuid: this.templateConfig().flowxUuid,
      prototypeInstanceMeta: this.collectionPrototypeInstanceMeta(),
      cache: this.#decorationCache,
    })
  )

  readonly = getCurrentReadOnlyState
  currentTemplateSequenceStore = toSignal(templateSequenceStore.stream$, { initialValue: [] })

  currentTemplateSequenceIds = computed<Set<string>>(() => {
    const currentTemplateSequence =
      this.currentTemplateSequenceStore()[this.currentTemplateSequenceStore().length - 1]
    return new Set(currentTemplateSequence?.map((ts) => `${ts.id}_${ts.context}`) ?? [])
  })

  computedTemplateConfigs = computed<TemplateConfig[]>(() => {
    if (this.readonly()) {
      return this.templateConfigWithCollectionMeta().filter((tc) => {
        // Only prune elements that have nodeDefinitionId and are CARD or CONTAINER
        if (
          isUserTaskRoot(tc) &&
          (tc.componentIdentifier === ComponentIdentifierEnum.CARD ||
            tc.componentIdentifier === ComponentIdentifierEnum.CONTAINER)
        ) {
          // Check if this element is in the currentTemplateSequence
          const templateKey = `${tc.id}_${tc.context}`
          return this.currentTemplateSequenceIds().has(templateKey)
        }
        // Keep all other elements (non-CARD/CONTAINER or elements without nodeDefinitionId)
        return true
      })
    }
    return this.templateConfigWithCollectionMeta()
  })

  readonly COMPONENTS_MAP = COMPONENT_WRAPPERS_MAP
  readonly CUSTOM_COMPONENTS_MAP = getCustomComponents()
  readonly ComponentIdentifierEnum = ComponentIdentifierEnum

  // Called from the template, so an unmemoized factory mints a pipeline per change-detection pass
  // and its markForCheck feeds the pass that re-invoked it.
  getCustomComponentDataAndActions$(
    templateConfig: CustomComponentTemplateConfig
  ): Observable<FlxCustomComponentData> {
    const memoized = this.#customComponentStreams.get(templateConfig)
    if (memoized) {
      return memoized
    }
    const stream$ = this.#buildCustomComponentDataAndActions$(templateConfig)
    this.#customComponentStreams.set(templateConfig, stream$)
    return stream$
  }

  getCustomComponentInjector(
    templateConfig: CustomComponentTemplateConfig,
    formService: FlxFormsService
  ): Injector {
    if (this.injectorMap.has(templateConfig.flowxUuid)) {
      return this.injectorMap.get(templateConfig.flowxUuid)
    }
    const injector = Injector.create({
      providers: [
        {
          provide: FLX_VALIDATOR_SERVICE,
          useValue: {
            validated$: formService.submittedForms$.pipe(
              map((forms) => {
                const { flowxUuid, reusableUiTemplateContext } = templateConfig
                const prefixedComponentUuid = reusableUiTemplateContext
                  ? `${reusableUiTemplateContext}_${flowxUuid}`
                  : flowxUuid
                return forms[prefixedComponentUuid]
              }),
              filter((forms) => !!forms)
            ),
            validate(isValid: boolean) {
              const { flowxUuid, reusableUiTemplateContext } = templateConfig
              const prefixedComponentUuid = reusableUiTemplateContext
                ? `${reusableUiTemplateContext}_${flowxUuid}`
                : flowxUuid
              formService.validateComponentStatus.set(prefixedComponentUuid, isValid)
              if (reusableUiTemplateContext) {
                if (
                  formService.reusableCustomComponentsMap.has(reusableUiTemplateContext) &&
                  formService.reusableCustomComponentsMap
                    .get(reusableUiTemplateContext)
                    .includes(flowxUuid)
                ) {
                  return
                }
                formService.reusableCustomComponentsMap.set(reusableUiTemplateContext, [
                  ...(formService.reusableCustomComponentsMap.get(reusableUiTemplateContext) || []),
                  flowxUuid,
                ])
              }
            },
            saveData(data: Record<string, any>) {
              const { flowxUuid, reusableUiTemplateContext } = templateConfig
              const prefixedComponentUuid = reusableUiTemplateContext
                ? `${reusableUiTemplateContext}_${flowxUuid}`
                : flowxUuid
              formService.setCustomComponentSubmitData(prefixedComponentUuid, data)
            },
          } satisfies ValidatorService,
        },
      ],
      parent: this.#injector,
    })
    this.injectorMap.set(templateConfig.flowxUuid, injector)
    return injector
  }

  trackTreeNode(childTc: TemplateConfig): string {
    return `${childTc.flowxUuid}-${hashCode(childTc.templateConfig?.map((tc) => tc.flowxUuid).join(','))}`
  }

  #buildCustomComponentDataAndActions$(
    templateConfig: CustomComponentTemplateConfig
  ): Observable<FlxCustomComponentData> {
    const {
      processInstanceUuid,
      inputKeys,
      prototypeInstanceMeta,
      reusableUiTemplateContext,
      flowxUuid,
    } = templateConfig
    const absoluteKeyPaths = collectionPrefix(inputKeys, prototypeInstanceMeta)
    if (reusableUiTemplateContext) {
      reusableCustomComponentsMap.set(
        reusableUiTemplateContext,
        `${reusableUiTemplateContext}_${flowxUuid}`
      )
    }
    return combineLatest({
      data: unfoldedProcessData$(processInstanceUuid ?? 'undefined', absoluteKeyPaths).pipe(
        map((data) => {
          if (prototypeInstanceMeta) {
            const newData = inputKeys.reduce((acc, key, index) => {
              acc[key] = _get(absoluteKeyPaths[index], data)
              return acc
            }, {})
            return _isEmpty(newData) ? null : newData
          }
          return data
        }),
        startWith(null)
      ),
      actionPairs: getActionsForTemplateConfig$(templateConfig),
    }).pipe(
      debounceTime(0), // limit emissions
      map(({ data, actionPairs }) => {
        const runners = new Map<string, ActionRunner>(
          actionPairs.map(({ action, actionFn }) => [
            action.name,
            (params) =>
              from(actionFn(snapshotActionParams(action, params, templateConfig), templateConfig)),
          ])
        )
        const runNamed = (
          name: string,
          params?: Record<string, unknown> | FormData
        ): Observable<void> =>
          runners.get(name)?.(params) ?? throwError(() => unknownCustomComponentActionError(name))
        return withDeprecatedSpread(
          {
            data,
            actions: actionPairs.map(({ action }) => action),
            actionsFn: Object.fromEntries(
              [...runners].map(([name, runner]) => [
                name,
                deprecatedAlias('actionsFn', 'runAction', runner),
              ])
            ),
            runAction: (action: string, params?: Record<string, unknown>) =>
              runNamed(action, params),
            runUploadAction: (action: string, file: Blob, params?: Record<string, unknown>) =>
              runNamed(action, buildUploadActionFormData(file, params)),
          },
          data,
          'data'
        )
      }),
      tap(() => this.#cdr.markForCheck()) // trigger change detection so that host custom component can react to data changes
    )
  }
}
